import { createElement } from 'react';
import { useTheme } from '@/assets/styles/themeMantine';

export interface LogoProps {
  /**
   * Size of the logo mark
   */
  size?: number;

  /**
   * Size of the logo type
   */
  width?: number;
  height?: number;

  /**
   * Theme
   */
  theme?: 'light' | 'dark' | 'brand';
  /**
   * Outline logo letter/s
   */
  withOutline?: boolean;
}

export const LogoType = ({
  theme = 'dark',
  withOutline = false,
  width = 136,
  height = 26,
}: LogoProps) => {
  const mantineTheme = useTheme();
  let color, dotColor;
  switch (theme) {
    case 'dark':
      color = 'black';
      dotColor = mantineTheme.colors.custom[1];
      break;
    case 'brand':
      color = 'white';
      // eslint-disable-next-line no-restricted-syntax
      dotColor = '#FFCF60';
      break;
    default:
      color = 'white';
      dotColor = 'white';
  }

  return createElement(
    'svg',
    {
      fill: color,
      width: width,
      height: height,
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: '0 0 136 26',
      // eslint-disable-next-line no-restricted-syntax
      stroke: withOutline ? '#A5AFB7' : 'none',
      strokeOpacity: 0.6,
      strokeWidth: 1.5,
    },
    createElement('path', {
      d: 'M12.274 4.2484C10.0326 4.2484 8.10249 5.0284 6.53326 6.5676C4.96402 8.1068 4.16895 10.0282 4.16895 12.2746C4.16895 14.5236 4.96402 16.4424 6.53326 17.9816C8.10249 19.5208 10.0326 20.3008 12.274 20.3008C14.5337 20.3008 16.4717 19.5208 18.0305 17.9816C19.5893 16.4424 20.3791 14.5236 20.3791 12.2746C20.3791 10.0282 19.5893 8.1068 18.0305 6.5676C16.4717 5.031 14.5337 4.2484 12.274 4.2484ZM15.5249 15.5688C14.6514 16.4372 13.5582 16.8792 12.274 16.8792C10.9925 16.8792 9.89664 16.4398 9.0231 15.5688C8.14956 14.6978 7.70494 13.5928 7.70494 12.2772C7.70494 10.9616 8.14694 9.854 9.0231 8.9856C9.89664 8.1172 10.9899 7.6752 12.274 7.6752C13.5582 7.6752 14.6514 8.1146 15.5249 8.9856C16.3985 9.854 16.8431 10.9616 16.8431 12.2772C16.8431 13.5902 16.3985 14.6978 15.5249 15.5688Z',
    }),
    createElement('path', {
      d: 'M29.1929 15.8054L25.0658 4.6384H21.2055L27.1974 19.9134H31.1597L37.1515 4.6384H33.2886L29.1929 15.8054Z',
    }),
    createElement('path', {
      d: 'M46.0804 4.2484C43.7188 4.2484 41.7572 5.0232 40.2481 6.5546C38.7391 8.0834 37.9754 10.01 37.9754 12.2746C37.9754 14.5808 38.7495 16.5178 40.2795 18.0258C41.8069 19.5364 43.8417 20.3008 46.3211 20.3008C49.2738 20.3008 51.5335 19.1802 53.0426 16.9702L53.1263 16.848L50.1997 15.1476L50.1264 15.2542C49.2921 16.4476 48.0211 17.0534 46.3498 17.0534C45.1363 17.0534 44.098 16.7544 43.2611 16.1642C42.4712 15.6078 41.9455 14.8096 41.6944 13.7904H53.6494L53.6677 13.6708C53.7514 13.0962 53.7906 12.649 53.7906 12.3006C53.7906 10.075 53.0635 8.1562 51.6277 6.5988C50.1918 5.0414 48.3244 4.2484 46.0804 4.2484ZM43.1224 8.424C43.8966 7.7896 44.8826 7.4672 46.0517 7.4672C47.0795 7.4672 47.9897 7.7714 48.756 8.3668C49.4831 8.9336 49.9669 9.7786 50.1971 10.8784H41.6683C41.8958 9.8514 42.3849 9.0272 43.1224 8.424Z',
    }),
    createElement('path', {
      d: 'M75.6343 11.4556L73.236 10.7198C72.4671 10.4832 71.8002 10.2232 71.2536 9.9424C70.7409 9.6798 70.4925 9.3366 70.4925 8.8946C70.4925 8.4266 70.6782 8.0834 71.06 7.8416C71.4576 7.5894 71.9623 7.4646 72.5534 7.4646C73.8742 7.4646 74.7608 7.9976 75.2629 9.0922L75.3257 9.23L78.3229 7.5608L78.2602 7.4412C77.7266 6.4428 76.9525 5.6524 75.9586 5.0908C74.9648 4.5318 73.8219 4.2484 72.556 4.2484C70.9606 4.2484 69.6085 4.6826 68.5309 5.538C67.4456 6.4012 66.8937 7.5582 66.8937 8.9752C66.8937 10.0048 67.184 10.868 67.7568 11.5388C68.3243 12.207 69.0383 12.675 69.8805 12.9324L72.2788 13.6682C73.0477 13.9048 73.712 14.1726 74.2534 14.4664C74.7634 14.742 75.0119 15.0852 75.0119 15.5142C75.0119 16.5516 74.2194 17.056 72.5874 17.056C70.9266 17.056 69.8752 16.38 69.3705 14.989L69.3129 14.833L66.2556 16.5984L66.3052 16.7128C66.7969 17.836 67.5999 18.7252 68.6852 19.357C69.768 19.9862 71.0809 20.306 72.5874 20.306C74.3005 20.306 75.7442 19.8822 76.8793 19.0502C78.0274 18.2078 78.6106 17.03 78.6106 15.5506C78.6106 14.4924 78.3229 13.6006 77.7554 12.8986C77.1905 12.1992 76.4765 11.7156 75.6343 11.4556Z',
    }),
    createElement('path', {
      d: 'M86.1115 0.392609L82.5755 1.4352V4.63841H79.8006V8.03401H82.5755V15.093C82.5755 17.0924 83.1431 18.4886 84.2651 19.2426C85.0549 19.773 86.2161 20.0408 87.7304 20.0408C88.3398 20.0408 89.0094 19.9966 89.7312 19.9108L89.8541 19.8952V16.718L89.7077 16.7258C88.8943 16.7648 88.2038 16.77 87.6546 16.7414C87.1394 16.7128 86.7471 16.575 86.4908 16.3306C86.2397 16.0888 86.1115 15.6728 86.1115 15.0956V8.03661H89.8541V4.64101H86.1115V0.392609Z',
    }),
    createElement('path', {
      d: 'M99.7534 4.2484C97.512 4.2484 95.5819 5.0284 94.0126 6.5676C92.4434 8.1068 91.6483 10.0282 91.6483 12.2746C91.6483 14.5236 92.4434 16.4424 94.0126 17.9816C95.5819 19.5208 97.512 20.3008 99.7534 20.3008C102.013 20.3008 103.951 19.5208 105.51 17.9816C107.069 16.4424 107.858 14.5236 107.858 12.2746C107.858 10.0282 107.069 8.1068 105.51 6.5676C103.951 5.031 102.013 4.2484 99.7534 4.2484ZM103.004 15.5688C102.131 16.4372 101.038 16.8792 99.7534 16.8792C98.4692 16.8792 97.376 16.4398 96.5025 15.5688C95.6289 14.7004 95.1869 13.5928 95.1869 12.2772C95.1869 10.9616 95.6289 9.854 96.5025 8.9856C97.376 8.1172 98.4692 7.6752 99.7534 7.6752C101.035 7.6752 102.131 8.1146 103.004 8.9856C103.878 9.854 104.32 10.9616 104.32 12.2772C104.32 13.5902 103.878 14.6978 103.004 15.5688Z',
    }),
    createElement('path', {
      d: 'M132.231 4.6384L128.46 15.275L124.066 4.6384H120.2L126.655 19.5L126.527 19.7548C126.135 20.7662 125.633 21.5176 125.031 21.9908C124.437 22.4562 123.637 22.6642 122.651 22.607L122.505 22.5992V25.9064L122.635 25.9142C122.805 25.9246 122.973 25.9298 123.14 25.9298C126.388 25.9298 128.742 24.0396 130.136 20.306L136 4.6384H132.231Z',
    }),
    createElement('path', {
      d: 'M2.62062 5.2832C4.06794 5.2832 5.24123 4.11681 5.24123 2.678C5.24123 1.23918 4.06794 0.0727997 2.62062 0.0727997C1.17329 0.0727997 0 1.23918 0 2.678C0 4.11681 1.17329 5.2832 2.62062 5.2832Z',
      fill: dotColor,
    }),
    createElement('path', {
      d: 'M60.4834 6.695V4.641H56.9448V19.916H60.4834V12.792C60.4938 9.9138 62.0997 8.5384 65.1309 8.216V4.3732C62.8006 4.4616 61.3621 5.5172 60.4834 6.695Z',
    }),
    createElement('path', {
      d: 'M114.376 6.695V4.641H110.837V19.916H114.376V12.792C114.386 9.9138 115.992 8.5384 119.024 8.216V4.3732C116.691 4.4616 115.255 5.5172 114.376 6.695Z',
    })
  );
};
